// Phase 282d — Super Admin tile: Quarterly Star Academy leaderboard.
// Renders live current-quarter ranking + previous locked podium.
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Trophy, Medal, Award, Zap, Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const RANK_ICONS = { 1: Trophy, 2: Medal, 3: Award };
const RANK_COLORS = {
  1: 'text-amber-500',
  2: 'text-slate-400',
  3: 'text-orange-500',
};

export default function QuarterlyStarLeaderboardTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(false);

  const fetchData = () => {
    setLoading(true);
    api.get('/super-admin/quarterly-star/leaderboard?top_n=10')
      .then(res => setData(res.data))
      .catch(() => toast.error('Could not load star academy leaderboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleLockIn = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Lock in the previous quarter\'s podium? This permanently freezes the top 3.')) return;
    setLocking(true);
    try {
      const res = await api.post('/super-admin/quarterly-star/lock-in', { dry_run: false });
      const r = res.data?.result || {};
      if (r.status === 'locked') {
        toast.success(`Quarter ${r.quarter_label} locked. Top: ${r.podium?.[0]?.academy_name || '—'}`);
        fetchData();
      } else if (r.status === 'already_locked') {
        toast.info(`Quarter ${r.quarter_label} was already locked.`);
      } else if (r.status === 'no_activations') {
        toast.warning(`No activations in ${r.quarter_label}. Nothing to lock.`);
      } else {
        toast.message(`Status: ${r.status}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Lock-in failed');
    } finally {
      setLocking(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="quarterly-star-tile-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-6 w-56" />
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  const live = data?.live || {};
  const leaderboard = live.leaderboard || [];
  const lastLocked = data?.last_locked;

  return (
    <Card data-testid="quarterly-star-leaderboard-tile">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-amber-700 dark:text-amber-400">
              <Trophy className="w-3 h-3" /> Star Academy · Live Leaderboard
            </div>
            <h3 className="text-base font-display font-semibold mt-1 text-slate-900 dark:text-slate-100">
              {live.quarter_label}
              <span className="text-xs text-muted-foreground font-normal ml-2">
                {live.quarter_start} → {live.quarter_end} · {live.elapsed_days}d elapsed
              </span>
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchData}
              data-testid="quarterly-star-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleLockIn}
              disabled={locking}
              data-testid="quarterly-star-lockin"
            >
              <Lock className="h-3.5 w-3.5 mr-1.5" />
              {locking ? 'Locking…' : 'Lock previous quarter'}
            </Button>
          </div>
        </div>

        {/* Live ranking */}
        {leaderboard.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No portal activations recorded yet this quarter.
          </p>
        ) : (
          <div className="space-y-2" data-testid="quarterly-star-rows">
            {leaderboard.map(row => {
              const Icon = RANK_ICONS[row.rank] || Zap;
              const color = RANK_COLORS[row.rank] || 'text-slate-400';
              return (
                <div
                  key={row.academy_id}
                  data-testid={`quarterly-star-row-${row.academy_id}`}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border ${
                    row.rank <= 3
                      ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-slate-800">
                    {row.rank <= 3 ? (
                      <Icon className={`h-5 w-5 ${color}`} />
                    ) : (
                      <span className="text-sm font-bold text-slate-400 tabular-nums">{row.rank}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {row.academy_name}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      <strong>{row.total}</strong> activations ·{' '}
                      <strong>{row.velocity}</strong>/day velocity
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Previously locked quarter */}
        {lastLocked ? (
          <div className="border-t pt-3 mt-3 border-slate-200 dark:border-slate-700">
            <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-2">
              Last locked: {lastLocked.quarter_label}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {(lastLocked.podium || []).slice(0, 3).map(p => {
                const Icon = RANK_ICONS[p.rank] || Trophy;
                const color = RANK_COLORS[p.rank] || 'text-amber-500';
                return (
                  <div
                    key={p.academy_id}
                    data-testid={`quarterly-star-locked-${p.rank}`}
                    className="flex items-center gap-2 p-2 rounded border border-slate-200 dark:border-slate-700"
                  >
                    <Icon className={`h-4 w-4 ${color} flex-shrink-0`} />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold truncate">{p.academy_name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {p.total} · {p.velocity}/day
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
